import React, { useEffect } from "react";
import { CryptoAlertData } from "@crypto-trading/common/browser";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import { formatExchangeDisplay } from "@/utils/formatExchange";
import { TokenIcon } from "@/components/TokenIcon";
import { Sparkline } from "@/components/ui/Sparkline";

/** Columns of the Analyze "Top Gainers / Losers" table, shared with its header. */
export const LEADERBOARD_ROW_GRID =
  "grid grid-cols-[20px_minmax(0,1fr)_78px_78px_116px] items-center gap-2.5";

interface LeaderboardTokenTileProps {
  alert: CryptoAlertData;
  onPriceUpdate?: (
    id: string,
    price: number | null,
    change: number | null
  ) => void;
  onAnalyze?: (symbol: string) => void;
  /** "row": a line of the desktop Analyze table; "compact": the phone list line. */
  variant?: "tile" | "row" | "compact";
  rank?: number;
}

export const LeaderboardTokenTile: React.FC<LeaderboardTokenTileProps> = ({
  alert,
  onPriceUpdate,
  onAnalyze,
  variant = "tile",
  rank,
}) => {
  // Always subscribe to live price - leaderboard has limited tiles (~20) and
  // viewport-based subscription causes flickering when sorting reorders the list
  const { price: livePrice } = useTokenLivePrice(
    alert.currency,
    "dynamic",
    true
  );

  const alertPrice = alert.price ?? null;
  const change =
    alertPrice && livePrice
      ? ((livePrice - alertPrice) / alertPrice) * 100
      : null;

  useEffect(() => {
    if (onPriceUpdate) {
      onPriceUpdate(alert.id, livePrice, change);
    }
  }, [alert.id, livePrice, change, onPriceUpdate]);

  const formatPrice = (price: number | null) => {
    if (!price) return "—";
    return `$${price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    })}`;
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleClick = () => onAnalyze?.(alert.currency);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  if (variant === "compact") {
    const up = (change ?? 0) >= 0;
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className="flex cursor-pointer items-center gap-2.5 border-b border-white/[0.06] px-3 py-3 transition-colors hover:bg-white/[0.03]"
      >
        <span className="w-4 text-sm text-[#d4d4d4]">{rank}</span>
        <TokenIcon symbol={alert.currency} logo={alert.tokenMetadata?.logo} size="sm" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-white">{alert.currency}</span>
          <span className="truncate text-[11px] text-[#a3a3a3]">
            {formatExchangeDisplay(alert.exchange, alert.exchangeType)} · {formatTimeAgo(alert.createdAt)}
          </span>
        </span>
        <span className="flex flex-col items-end">
          <span className="text-sm font-semibold tabular-nums text-white">{formatPrice(livePrice)}</span>
          <span className={`text-[11px] tabular-nums ${change === null ? "text-[#a3a3a3]" : up ? "text-[#2dcc89]" : "text-[#f97066]"}`}>
            {change === null ? "—" : `${up ? "+" : ""}${change.toFixed(2)}%`}
          </span>
        </span>
        {alert.sparkline && alert.sparkline.length > 1 && (
          <Sparkline values={alert.sparkline} width={48} height={28} />
        )}
      </div>
    );
  }

  if (variant === "row") {
    const up = (change ?? 0) >= 0;
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`${LEADERBOARD_ROW_GRID} cursor-pointer border-b border-white/[0.06] px-4 py-3.5 text-sm transition-colors hover:bg-white/[0.03]`}
      >
        <span className="text-[#d4d4d4]">{rank}</span>
        <span className="flex min-w-0 items-center gap-3">
          <TokenIcon symbol={alert.currency} logo={alert.tokenMetadata?.logo} size="md" />
          <span className="flex min-w-0 flex-col">
            <span className="truncate font-medium text-white">{alert.currency}</span>
            <span className="truncate text-xs text-[#a3a3a3]">
              {formatExchangeDisplay(alert.exchange, alert.exchangeType)} · {formatTimeAgo(alert.createdAt)}
            </span>
          </span>
        </span>
        <span className="truncate tabular-nums text-white">{formatPrice(livePrice)}</span>
        <span className="truncate tabular-nums text-white">{formatPrice(alertPrice)}</span>
        <span className="flex items-center justify-end gap-2">
          <span className={`tabular-nums font-medium ${change === null ? "text-[#a3a3a3]" : up ? "text-[#2dcc89]" : "text-[#f97066]"}`}>
            {change === null ? "—" : `${up ? "+" : ""}${change.toFixed(2)}%`}
          </span>
          {alert.sparkline && alert.sparkline.length > 1 && (
            <Sparkline values={alert.sparkline} width={40} height={20} />
          )}
        </span>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="p-4 bg-secondary border border-border rounded-xl hover:bg-tertiary cursor-pointer transition-colors"
    >
      {/* Header: icon + symbol + exchange info */}
      <div className="flex items-center gap-3 min-w-0 mb-3">
        <TokenIcon
          symbol={alert.currency}
          logo={alert.tokenMetadata?.logo}
          size="md"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-semibold text-text-primary truncate">
              {alert.currency}
            </h3>
            <span
              className={`shrink-0 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                alert.type === "delisting"
                  ? "bg-red-500/15 text-red-400"
                  : "bg-green-500/15 text-green-400"
              }`}
            >
              {alert.type === "delisting" ? "Delisting" : "Listing"}
            </span>
          </div>
          <p className="text-xs text-text-secondary">
            {formatExchangeDisplay(alert.exchange, alert.exchangeType)} ·{" "}
            {formatTimeAgo(alert.createdAt)}
          </p>
        </div>
      </div>
      {/* Stats grid - inspired by CryptoAlertTile */}
      <div className="grid grid-cols-3 gap-4 border-t border-border pt-3">
        <div className="border-r border-border pr-4">
          <p className="text-xs text-text-secondary mb-1">Alert</p>
          <p className="text-sm font-medium text-text-primary font-mono tabular-nums truncate">
            {formatPrice(alertPrice)}
          </p>
        </div>
        <div className="border-r border-border pr-4">
          <p className="text-xs text-text-secondary mb-1">Now</p>
          <p className="text-sm font-medium text-text-primary font-mono tabular-nums truncate">
            {formatPrice(livePrice)}
          </p>
        </div>
        <div>
          <p className="text-xs text-text-secondary mb-1">Change</p>
          {change === null ? (
            <p className="text-sm text-text-secondary">—</p>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="flex-shrink-0 w-6 h-4 flex items-center justify-center">
                <img
                  src={change >= 0 ? "/icons/profit.png" : "/icons/loss.png"}
                  alt={change >= 0 ? "Up" : "Down"}
                  className="w-6 h-4 object-contain"
                />
              </span>
              <span
                className={`text-sm font-semibold font-mono tabular-nums ${
                  change >= 0 ? "text-green-400" : "text-red-400"
                }`}
              >
                {change >= 0 ? "+" : ""}
                {change.toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
